/**
 * The lyric timing of a catalog song (spec `2026-10-05-music24-full-videos-and-lyric-sync-design.md`
 * §5.5): the time of each lyric line in the master, from music24-media `POST /v1/align` and from the
 * edits of the members. music24-api stores it in `AccountCatalogItemModel.lyricTiming` (JSON); the
 * lyric video, the lyric moment and the captions of the music video read it; the song files sheet
 * shows and edits it.
 *
 * The file has the shape, the line split, the read helpers and the edit rules. music24-api and the
 * web app use the same functions, so the editor and the server agree. Two rules (owner decision
 * 2026-10-05): a lyrics change keeps the edits of the lines whose text did not change; a new master
 * drops all edits (the times belong to the old audio).
 *
 * All times are in milliseconds from the start of the master. The file has no imports, so every
 * project can use it. Every function is pure.
 */

export const LYRIC_TIMING_VERSION = 1;

export const LYRIC_SYNC_STATUSES = ['syncing', 'synced', 'failed'] as const;
export type LyricSyncStatus = (typeof LYRIC_SYNC_STATUSES)[number];

/** The sync state of a catalog row: `none` when the column is null (no lyrics or no master yet). */
export type LyricSyncState = 'none' | LyricSyncStatus;

/**
 * Why a sync failed. `noVocals`, `noMatch`, `tooLong` and `decode` are the 422 reasons of
 * `/v1/align`; `error` is every other failure on the last attempt, and the stranded-sync sweep.
 */
export const LYRIC_TIMING_FAIL_REASONS = ['noVocals', 'noMatch', 'tooLong', 'decode', 'error'] as const;
export type LyricTimingFailReason = (typeof LYRIC_TIMING_FAIL_REASONS)[number];

/** The start and the end of one word, in ms. Null for a word with no time. */
export type LyricWordTime = [number, number] | null;

export interface LyricTimingLine {
  /** The index in `lyricLinesOf(lyrics)`. */
  index: number;
  text: string;
  /** A blank line or a section label comes before the line: a new lyric block starts here. */
  gapBefore: boolean;
  startMs: number | null;
  endMs: number | null;
  /** The mean word score, 0..1. Null for a time a member set, and for a line with no time. */
  score: number | null;
  /** A member set this start. */
  edited: boolean;
  /** One entry per word of `lyricWordsOf(text)`. Null when the line has no word times. */
  words: LyricWordTime[] | null;
}

export interface LyricTimingSource {
  aligner: 'stable-ts';
  model: 'whisper-small';
  passes: 1 | 2;
}

export interface LyricTimingV1 {
  version: 1;
  status: LyricSyncStatus;
  failReason: LyricTimingFailReason | null;
  /** +1 on every write; the editor sends it back (optimistic lock). */
  revision: number;
  /** The SHA-256 (hex) of the master that the times belong to. */
  masterSha256: string;
  /** The SHA-256 (hex, UTF-8) of `canonicalLyrics(lyrics)`: the lines that the times belong to. */
  lyricsSha256: string;
  language: string | null;
  source: LyricTimingSource | null;
  /** ISO time of the last aligner result. */
  syncedAt: string | null;
  lines: LyricTimingLine[];
}

/** The short form for the song answer (`serializeCatalogSong`); the catalog list never sends the lines. */
export interface LyricTimingSummary {
  status: LyricSyncStatus;
  failReason: LyricTimingFailReason | null;
  lineCount: number;
  timedCount: number;
  editedCount: number;
  checkCount: number;
  syncedAt: string | null;
  revision: number;
}

/** A line is weak below this mean word score (spec §5.4 step 5). */
export const LYRIC_WEAK_SCORE = 0.3;
/** A line is weak when it is shorter than this. */
export const LYRIC_WEAK_MIN_LENGTH_MS = 200;
/** A start must be at least this much after the start of the timed line above. */
export const LYRIC_LINE_MIN_STEP_MS = 100;
/** A lyric video needs at least this share of the lines timed, else `LYRICS_NOT_SYNCED`. */
export const LYRIC_VIDEO_MIN_TIMED_SHARE = 0.5;
/** A gap longer than this between two lines shows the title and the artist, small (spec §4.3). */
export const LYRIC_GAP_TITLE_MS = 4000;
/** The most lines that one `PATCH .../lyric-timing` takes. */
export const LYRIC_TIMING_PATCH_MAX_LINES = 200;
/** The most re-syncs of one row in one hour (`POST .../lyric-timing/resync`, else 429). */
export const LYRIC_RESYNC_PER_HOUR = 10;

/**
 * The display lead of a lyric line (owner, 2026-10-06: "the new line starts slightly before the
 * line is marked"). Every player and every render shows a line this much BEFORE its stored start:
 * the web song page marks the line that plays, and the lyric video, the lyric moment and the music
 * video captions (`buildLyricLayers` in music24-api) move a line in this much earlier. The stored
 * times do not change, so an old timing gets the lead at once, with no re-sync. A reader needs the
 * line a moment before the voice starts it, and the forced alignment tends to put a start on the
 * first clear vowel, a little after the consonant. The native apps mirror the value
 * (`LyricTimingRules.lineLeadMs` in iOS and Android); change the three together.
 */
export const LYRIC_LINE_LEAD_SECONDS = 0.2;
/** `LYRIC_LINE_LEAD_SECONDS` in ms. */
export const LYRIC_LINE_LEAD_MS = Math.round(LYRIC_LINE_LEAD_SECONDS * 1000);

/**
 * "Set" at the play position (the sync editor on the web, iOS and Android) stores the play
 * position minus this much. A person who taps along with a song they know taps a little after the
 * voice starts; without the offset a tapped line would be later than an aligned one. The editors
 * read the exact play position at the tap (not the last 250 ms tick of the player). The display
 * lead applies to a tapped time too, like to every stored time.
 */
export const LYRIC_TAP_REACTION_MS = 100;

/** The start that "Set" stores for a tap at `playMs`: `playMs - LYRIC_TAP_REACTION_MS`, never below 0. */
export function tappedLineStartMs(playMs: number): number {
  return Math.max(0, Math.round(playMs - LYRIC_TAP_REACTION_MS));
}

/**
 * The error codes of the lyric timing routes (`cause.code`):
 * - `M24_LYRIC_TIMING_ORDER` (400): a start before the start of the timed line above + 100 ms, past
 *   the song, negative, or an index that the timing does not have.
 * - `M24_LYRIC_TIMING_STALE` (409): the revision is not the current one. Read the timing again.
 * - `M24_LYRIC_TIMING_SYNCING` (409): a sync runs; the timing cannot be edited now.
 */
export type LyricTimingErrorCode = 'M24_LYRIC_TIMING_ORDER' | 'M24_LYRIC_TIMING_STALE' | 'M24_LYRIC_TIMING_SYNCING';

/** One lyric line before alignment. */
export interface LyricLine {
  index: number;
  text: string;
  gapBefore: boolean;
}

const SECTION_LABEL = /^\[.*\]$/;

/**
 * The lyric lines of a lyrics text (spec §5.4 step 2). music24-media splits the text with the same
 * rule (`music24_media/lyric_lines.py`); keep the two equal. Both test suites read the cases of
 * `__fixtures__/lyricLinesParity.json`:
 *
 * 1. Split on `\n` (a `\r\n` or `\r` is a line end too). Trim each line (Python `str.strip()`).
 * 2. A blank line and a section label (`[Chorus]`: a line that starts with `[` and ends with `]`)
 *    are not lyric lines.
 * 3. Each other line is a lyric line, numbered from 0 in order.
 * 4. `gapBefore` is true when a blank line or a section label comes between the line and the lyric
 *    line before it. The first line has `gapBefore: false`.
 */
export function lyricLinesOf(lyrics: string | null | undefined): LyricLine[] {
  if (lyrics === null || lyrics === undefined) {
    return [];
  }
  const lines: LyricLine[] = [];
  let gap = false;
  for (const raw of lyrics.split(/\r\n|\r|\n/)) {
    const text = raw.trim();
    if (text === '' || SECTION_LABEL.test(text)) {
      gap = true;
      continue;
    }
    lines.push({ index: lines.length, text, gapBefore: lines.length > 0 && gap });
    gap = false;
  }
  return lines;
}

/**
 * The text that `lyricsSha256` hashes (SHA-256, hex, of the UTF-8 bytes): the lyric lines joined by
 * `\n`, with one empty line before each line with `gapBefore`. So an edit of a label, of the spaces
 * or of the blank lines inside a block does not change the hash; a change of a line or of a block
 * boundary does. An empty string for no lyrics.
 */
export function canonicalLyrics(lyrics: string | null | undefined): string {
  return lyricLinesOf(lyrics)
    .map((line) => (line.gapBefore ? `\n${line.text}` : line.text))
    .join('\n');
}

/** The words of a line: split on whitespace. `words` of a timed line has one entry per word. */
export function lyricWordsOf(text: string): string[] {
  return text.split(/\s+/).filter((word) => word !== '');
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isTime(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isNullableTime(value: unknown): value is number | null {
  return value === null || isTime(value);
}

function isOneOf<T extends string>(list: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (list as readonly string[]).includes(value);
}

function parseWords(value: unknown): LyricWordTime[] | null | undefined {
  if (value === null) {
    return null;
  }
  if (!Array.isArray(value)) {
    return undefined;
  }
  const words: LyricWordTime[] = [];
  for (const word of value) {
    if (word === null) {
      words.push(null);
    } else if (Array.isArray(word) && word.length === 2 && isTime(word[0]) && isTime(word[1])) {
      words.push([word[0], word[1]]);
    } else {
      return undefined;
    }
  }
  return words;
}

function parseLine(value: unknown): LyricTimingLine | null {
  if (!isObject(value)) {
    return null;
  }
  const { index, text, gapBefore, startMs, endMs, score, edited } = value;
  const words = parseWords(value.words);
  if (
    typeof index !== 'number' ||
    !Number.isInteger(index) ||
    index < 0 ||
    typeof text !== 'string' ||
    typeof gapBefore !== 'boolean' ||
    !isNullableTime(startMs) ||
    !isNullableTime(endMs) ||
    !(score === null || (typeof score === 'number' && score >= 0 && score <= 1)) ||
    typeof edited !== 'boolean' ||
    words === undefined
  ) {
    return null;
  }
  return { index, text, gapBefore, startMs, endMs, score, edited, words };
}

function parseSource(value: unknown): LyricTimingSource | null | undefined {
  if (value === null) {
    return null;
  }
  if (
    !isObject(value) ||
    value.aligner !== 'stable-ts' ||
    value.model !== 'whisper-small' ||
    (value.passes !== 1 && value.passes !== 2)
  ) {
    return undefined;
  }
  return { aligner: 'stable-ts', model: 'whisper-small', passes: value.passes === 2 ? 2 : 1 };
}

/**
 * The stored value of `lyricTiming` as a `LyricTimingV1`. An unknown version or a broken value
 * reads as null (as "never synced"), so old or hand-written JSON never reaches a renderer. The
 * lines must have the indexes 0, 1, 2, ... in order.
 */
export function parseLyricTiming(json: unknown): LyricTimingV1 | null {
  if (!isObject(json) || json.version !== LYRIC_TIMING_VERSION) {
    return null;
  }
  const { status, failReason, revision, masterSha256, lyricsSha256, language, syncedAt } = json;
  const source = parseSource(json.source);
  if (
    !isOneOf(LYRIC_SYNC_STATUSES, status) ||
    !(failReason === null || isOneOf(LYRIC_TIMING_FAIL_REASONS, failReason)) ||
    typeof revision !== 'number' ||
    !Number.isInteger(revision) ||
    revision < 0 ||
    typeof masterSha256 !== 'string' ||
    typeof lyricsSha256 !== 'string' ||
    !(language === null || typeof language === 'string') ||
    !(syncedAt === null || typeof syncedAt === 'string') ||
    source === undefined ||
    !Array.isArray(json.lines)
  ) {
    return null;
  }
  const lines: LyricTimingLine[] = [];
  for (const raw of json.lines) {
    const line = parseLine(raw);
    if (line === null || line.index !== lines.length) {
      return null;
    }
    lines.push(line);
  }
  return {
    version: 1,
    status,
    failReason,
    revision,
    masterSha256,
    lyricsSha256,
    language,
    source,
    syncedAt,
    lines,
  };
}

/** The sync state of a row from its stored `lyricTiming` (the catalog list `song.lyricSyncStatus`). */
export function lyricSyncStateOf(json: unknown): LyricSyncState {
  return parseLyricTiming(json)?.status ?? 'none';
}

/** True when the line has a start. */
export function isTimedLine(line: LyricTimingLine): boolean {
  return line.startMs !== null;
}

/**
 * True when a member should check the line (the "check" mark): it is not edited, and it has no
 * time, or its score is under `LYRIC_WEAK_SCORE`, or it is shorter than `LYRIC_WEAK_MIN_LENGTH_MS`.
 */
export function needsCheck(line: LyricTimingLine): boolean {
  if (line.edited) {
    return false;
  }
  if (line.startMs === null) {
    return true;
  }
  if (line.score !== null && line.score < LYRIC_WEAK_SCORE) {
    return true;
  }
  return line.endMs !== null && line.endMs - line.startMs < LYRIC_WEAK_MIN_LENGTH_MS;
}

export interface LyricTimingCounts {
  lineCount: number;
  timedCount: number;
  editedCount: number;
  /** The lines that `needsCheck` marks. */
  checkCount: number;
}

export function timingCounts(timing: LyricTimingV1): LyricTimingCounts {
  return {
    lineCount: timing.lines.length,
    timedCount: timing.lines.filter(isTimedLine).length,
    editedCount: timing.lines.filter((line) => line.edited).length,
    checkCount: timing.lines.filter(needsCheck).length,
  };
}

/** The summary for the song answer; null for no timing. */
export function lyricTimingSummary(timing: LyricTimingV1 | null): LyricTimingSummary | null {
  if (timing === null) {
    return null;
  }
  return {
    status: timing.status,
    failReason: timing.failReason,
    ...timingCounts(timing),
    syncedAt: timing.syncedAt,
    revision: timing.revision,
  };
}

/** The share of the lines with a time, 0..1 (0 for no lines). A lyric video needs `LYRIC_VIDEO_MIN_TIMED_SHARE`. */
export function timedShare(timing: LyricTimingV1): number {
  return timing.lines.length === 0 ? 0 : timing.lines.filter(isTimedLine).length / timing.lines.length;
}

/**
 * True when every line from `lineFrom` to `lineTo` (both included) exists and has a time. A lyric
 * moment needs it (else 400 `M24_LYRIC_LINES_NOT_TIMED` at create).
 */
export function areLinesTimed(timing: LyricTimingV1, lineFrom: number, lineTo: number): boolean {
  if (!Number.isInteger(lineFrom) || !Number.isInteger(lineTo) || lineFrom < 0 || lineTo < lineFrom) {
    return false;
  }
  if (lineTo >= timing.lines.length) {
    return false;
  }
  return timing.lines.slice(lineFrom, lineTo + 1).every(isTimedLine);
}

/**
 * The time that a line stays on screen in a lyric video (spec §4.3): from its start to the start of
 * the next timed line. When the line has an end and the next start comes more than
 * `LYRIC_GAP_TITLE_MS` after that end, the line leaves at its end (the title shows in the gap). The
 * last timed line leaves at its end, else `LYRIC_GAP_TITLE_MS` after its start. Null for a line with
 * no time.
 */
export function lineScreenSpan(timing: LyricTimingV1, index: number): { startMs: number; endMs: number } | null {
  const line = timing.lines[index];
  if (line === undefined || line.startMs === null) {
    return null;
  }
  const next = timing.lines.slice(index + 1).find(isTimedLine);
  if (next === undefined || next.startMs === null) {
    return { startMs: line.startMs, endMs: line.endMs ?? line.startMs + LYRIC_GAP_TITLE_MS };
  }
  if (line.endMs !== null && next.startMs - line.endMs > LYRIC_GAP_TITLE_MS) {
    return { startMs: line.startMs, endMs: line.endMs };
  }
  return { startMs: line.startMs, endMs: next.startMs };
}

/** The line on screen at `sec` seconds of the song (`lineScreenSpan`); null in a gap. */
export function lineAt(timing: LyricTimingV1, sec: number): LyricTimingLine | null {
  const ms = sec * 1000;
  for (let index = timing.lines.length - 1; index >= 0; index -= 1) {
    const span = lineScreenSpan(timing, index);
    if (span !== null && span.startMs <= ms) {
      return ms < span.endMs ? (timing.lines[index] ?? null) : null;
    }
  }
  return null;
}

/**
 * The line to MARK as sung at `sec` seconds of the song: `lineAt` with the display lead
 * (`LYRIC_LINE_LEAD_SECONDS`), so a line lights up slightly before its stored start and leaves
 * slightly before its screen end. Every player highlight uses this, never `lineAt` alone.
 */
export function activeLineAt(
  timing: LyricTimingV1,
  sec: number,
  leadSec: number = LYRIC_LINE_LEAD_SECONDS
): LyricTimingLine | null {
  return lineAt(timing, sec + leadSec);
}

/** The time of one word when a line has no end and no word times: 400 ms per word. */
export const LYRIC_FALLBACK_WORD_MS = 400;

/**
 * The start and the end (ms) of every word of a timed line, one pair per word of
 * `lyricWordsOf(line.text)` (spec §4.5: every word lights up at its time). A word with its own time
 * keeps it, inside the line. A run of words with no time shares the time between the word before it
 * (else the line start) and the word after it (else the line end) in equal parts. A line with no
 * word times shares its whole time in equal parts. The line end is `line.endMs`, else `fallbackEndMs`
 * (the end of the line on screen), else `LYRIC_FALLBACK_WORD_MS` per word. Every word has at least
 * 1 ms. Empty for a line with no start or no words. Pure.
 */
export function wordTimesOf(line: LyricTimingLine, fallbackEndMs?: number): Array<[number, number]> {
  const words = lyricWordsOf(line.text);
  if (line.startMs === null || words.length === 0) {
    return [];
  }
  const start = line.startMs;
  const wantedEnd = line.endMs ?? fallbackEndMs ?? start + words.length * LYRIC_FALLBACK_WORD_MS;
  const end = Math.max(wantedEnd, start + words.length);
  const own: Array<[number, number] | null> =
    line.words !== null && line.words.length === words.length
      ? line.words.map((word) =>
          word === null ? null : [Math.min(Math.max(word[0], start), end), Math.min(Math.max(word[1], start), end)]
        )
      : words.map(() => null);

  const result: Array<[number, number]> = [];
  let index = 0;
  while (index < own.length) {
    const word = own[index];
    if (word !== null && word !== undefined) {
      const from = Math.max(word[0], result[result.length - 1]?.[1] ?? start);
      result.push([Math.round(from), Math.round(Math.max(word[1], from + 1))]);
      index += 1;
      continue;
    }
    // A run of words with no time: from the end of the word before to the start of the word after.
    let runEnd = index;
    while (runEnd < own.length && own[runEnd] === null) {
      runEnd += 1;
    }
    const from = result[result.length - 1]?.[1] ?? start;
    const to = Math.max(own[runEnd]?.[0] ?? end, from + (runEnd - index));
    const step = (to - from) / (runEnd - index);
    for (let k = index; k < runEnd; k += 1) {
      const wordStart = from + step * (k - index);
      result.push([Math.round(wordStart), Math.round(Math.max(wordStart + step, wordStart + 1))]);
    }
    index = runEnd;
  }
  return result;
}

function untimed(line: LyricLine): LyricTimingLine {
  return { ...line, startMs: null, endMs: null, score: null, edited: false, words: null };
}

/**
 * The key of each line for "the k-th line with this text": the text and how many lines with the
 * same text come before it. A lyrics change keeps the edit of a line whose key is still there.
 */
function occurrenceKeys(lines: ReadonlyArray<{ text: string }>): string[] {
  const seen = new Map<string, number>();
  return lines.map(({ text }) => {
    const count = seen.get(text) ?? 0;
    seen.set(text, count + 1);
    return `${count}\u0000${text}`;
  });
}

/**
 * Makes the order valid: an edited line keeps its time; a line that is not edited and starts before
 * the timed line above + `LYRIC_LINE_MIN_STEP_MS`, or less than that before the next edited line,
 * loses its time. Then each end is cut to the next start. The edits themselves are in order (the
 * edit rule and the merge keep them so).
 */
function repairOrder(lines: LyricTimingLine[]): LyricTimingLine[] {
  const result = lines.map(copyLine);
  const editedStarts = result.map((line) => (line.edited ? line.startMs : null));
  let lastStart: number | null = null;
  for (const [index, line] of result.entries()) {
    if (line.startMs === null) {
      continue;
    }
    const nextEdited = editedStarts.slice(index + 1).find((start): start is number => start !== null);
    const tooEarly = lastStart !== null && line.startMs < lastStart + LYRIC_LINE_MIN_STEP_MS;
    const tooLate = nextEdited !== undefined && line.startMs > nextEdited - LYRIC_LINE_MIN_STEP_MS;
    if (!line.edited && (tooEarly || tooLate)) {
      Object.assign(line, { startMs: null, endMs: null, score: null, words: null });
      continue;
    }
    lastStart = line.startMs;
  }
  cutEndsToNextStart(result);
  return result;
}

function copyLine(line: LyricTimingLine): LyricTimingLine {
  return { ...line, words: line.words === null ? null : [...line.words] };
}

/** Cuts the end of each line to the start of the next timed line, in place. */
function cutEndsToNextStart(lines: LyricTimingLine[]): void {
  for (const [index, line] of lines.entries()) {
    const nextStart = lines.slice(index + 1).find(isTimedLine)?.startMs ?? null;
    if (line.endMs !== null && nextStart !== null && line.endMs > nextStart) {
      line.endMs = nextStart;
    }
  }
}

export interface BeginLyricSyncInput {
  /** The SHA-256 of the master that the sync aligns. */
  masterSha256: string;
  /** The lyrics text of the row. */
  lyrics: string;
  /** SHA-256 (hex) of `canonicalLyrics(lyrics)`. */
  lyricsSha256: string;
  /**
   * True for a lyrics change: the lines whose text did not change keep their edits. False for
   * Re-sync (all edits go). A new master always drops all edits, whatever this says.
   */
  keepEdits: boolean;
}

/**
 * The timing to store when a sync starts (status `syncing`; spec §5.6). The lines are the lyric
 * lines of the new text with no time; with `keepEdits` and the same master, a line keeps the edit of
 * the k-th line with the same text in `previous`. The revision is one above the previous one.
 */
export function beginLyricSync(previous: LyricTimingV1 | null, input: BeginLyricSyncInput): LyricTimingV1 {
  const fresh = lyricLinesOf(input.lyrics).map(untimed);
  const sameMaster = previous !== null && previous.masterSha256 === input.masterSha256;
  let lines = fresh;
  if (input.keepEdits && sameMaster && previous !== null) {
    const edits = new Map<string, LyricTimingLine>();
    occurrenceKeys(previous.lines).forEach((key, index) => {
      const line = previous.lines[index];
      if (line?.edited === true) {
        edits.set(key, line);
      }
    });
    const keys = occurrenceKeys(fresh);
    lines = repairOrder(
      fresh.map((line, index) => {
        const edit = edits.get(keys[index] ?? '');
        return edit === undefined
          ? line
          : { ...line, startMs: edit.startMs, endMs: edit.endMs, score: null, edited: true, words: edit.words };
      })
    );
  }
  return {
    version: 1,
    status: 'syncing',
    failReason: null,
    revision: (previous?.revision ?? 0) + 1,
    masterSha256: input.masterSha256,
    lyricsSha256: input.lyricsSha256,
    language: sameMaster ? (previous?.language ?? null) : null,
    source: null,
    syncedAt: sameMaster ? (previous?.syncedAt ?? null) : null,
    lines,
  };
}

/** One line of the aligner answer (the `/v1/align` line, without its text checks). */
export interface AlignedLyricLine {
  index: number;
  startMs: number | null;
  endMs: number | null;
  score: number | null;
  words: LyricWordTime[] | null;
}

export interface CompleteLyricSyncInput {
  language: string | null;
  passes: 1 | 2;
  lines: AlignedLyricLine[];
  /** ISO time of the result. */
  syncedAt: string;
}

/**
 * The timing to store when the aligner answers for `current` (the `syncing` timing of
 * `beginLyricSync`; the caller checks first that the row still has its master and lyrics). Each
 * line takes the aligned time of its index; an edited line keeps its edit. Then a line that is not
 * edited and breaks the order loses its time. Status `synced`, revision + 1.
 */
export function completeLyricSync(current: LyricTimingV1, result: CompleteLyricSyncInput): LyricTimingV1 {
  const aligned = new Map(result.lines.map((line) => [line.index, line]));
  const lines = repairOrder(
    current.lines.map((line) => {
      if (line.edited) {
        return line;
      }
      const match = aligned.get(line.index);
      if (match === undefined) {
        return { ...line, startMs: null, endMs: null, score: null, words: null };
      }
      return { ...line, startMs: match.startMs, endMs: match.endMs, score: match.score, words: match.words };
    })
  );
  return {
    ...current,
    status: 'synced',
    failReason: null,
    revision: current.revision + 1,
    language: result.language,
    source: { aligner: 'stable-ts', model: 'whisper-small', passes: result.passes },
    syncedAt: result.syncedAt,
    lines,
  };
}

/** The timing to store when the sync fails: the lines stay (edits included), status `failed`, revision + 1. */
export function failLyricSync(current: LyricTimingV1, reason: LyricTimingFailReason): LyricTimingV1 {
  return { ...current, status: 'failed', failReason: reason, revision: current.revision + 1 };
}

/** One change of `PATCH .../lyric-timing`: the new start of a line, or null to clear its time. */
export interface LyricLineStartEdit {
  index: number;
  startMs: number | null;
}

export type EditLyricTimingResult =
  | { ok: true; timing: LyricTimingV1 }
  | { ok: false; code: LyricTimingErrorCode; index?: number };

/**
 * The edit rule of `PATCH .../lyric-timing` (spec §5.9), for the server and for the optimistic
 * state of the web editor:
 *
 * - `SYNCING` while a sync runs; `STALE` when `revision` is not the current one.
 * - Each edited line: `startMs` is the new start, the words and the end move by the same delta (the
 *   end is cut to the song end); a line with no time before gets no end and no words. `edited:
 *   true`, `score: null`. A null start clears the time of the line (also `edited: true`).
 * - `ORDER` (with `index`) for an unknown index, a negative start, a start past `durationMs`, or a
 *   start before the start of the timed line above + `LYRIC_LINE_MIN_STEP_MS` (after all edits).
 * - The end of the line before becomes the new start when they overlap.
 * - A `failed` timing becomes `synced` when at least one line has a time.
 * - Revision + 1.
 */
export function editLyricTiming(
  timing: LyricTimingV1,
  edit: { revision: number; lines: LyricLineStartEdit[] },
  options: { durationMs: number }
): EditLyricTimingResult {
  if (timing.status === 'syncing') {
    return { ok: false, code: 'M24_LYRIC_TIMING_SYNCING' };
  }
  if (edit.revision !== timing.revision) {
    return { ok: false, code: 'M24_LYRIC_TIMING_STALE' };
  }
  const lines = timing.lines.map(copyLine);
  for (const change of edit.lines) {
    const line = lines[change.index];
    if (line === undefined) {
      return { ok: false, code: 'M24_LYRIC_TIMING_ORDER', index: change.index };
    }
    if (change.startMs === null) {
      Object.assign(line, { startMs: null, endMs: null, score: null, edited: true, words: null });
      continue;
    }
    if (!isTime(change.startMs) || change.startMs > options.durationMs) {
      return { ok: false, code: 'M24_LYRIC_TIMING_ORDER', index: change.index };
    }
    const startMs = Math.round(change.startMs);
    const delta = line.startMs === null ? null : startMs - line.startMs;
    const cut = (ms: number): number => Math.min(Math.max(0, ms + (delta ?? 0)), options.durationMs);
    Object.assign(line, {
      startMs,
      endMs: delta === null || line.endMs === null ? null : cut(line.endMs),
      score: null,
      edited: true,
      words:
        delta === null || line.words === null
          ? null
          : line.words.map((word) => (word === null ? null : ([cut(word[0]), cut(word[1])] as [number, number]))),
    });
  }
  let lastStart: number | null = null;
  for (const line of lines) {
    if (line.startMs === null) {
      continue;
    }
    if (lastStart !== null && line.startMs < lastStart + LYRIC_LINE_MIN_STEP_MS) {
      return { ok: false, code: 'M24_LYRIC_TIMING_ORDER', index: line.index };
    }
    lastStart = line.startMs;
  }
  cutEndsToNextStart(lines);
  const anyTimed = lines.some(isTimedLine);
  const status: LyricSyncStatus = timing.status === 'failed' && anyTimed ? 'synced' : timing.status;
  return {
    ok: true,
    timing: {
      ...timing,
      status,
      failReason: status === 'failed' ? timing.failReason : null,
      revision: timing.revision + 1,
      lines,
    },
  };
}
